import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  Collapse,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Box,
} from "@mui/material";
import {
  ExpandLess,
  ExpandMore,
  Home as HomeIcon,
  Settings as SettingsIcon,
  Lock as LockIcon,
  Group as GroupIcon,
  List as ListIcon,
  Person as PersonIcon,
  LockOpen as LockOpenIcon,
  Link as LinkIcon,
  AdminPanelSettings as AdminIcon,
} from "@mui/icons-material";

import { connect } from "react-redux";
import { ApplicationState } from "../store";
import * as Auth0State from "../store/Auth0";
import { LoginForm } from "./shared/LoginForm";
import TenantPicker from "./Tenants/TenantPicker";
import { MeState } from "../store/Me";
import { isAdmin } from "../helpers";
import Logo from "./logo.png";

type OwnProps = {};
type Props = OwnProps & {
  auth0: Auth0State.Auth0State;
  me: MeState;
};

const NavMenuComponent = (props: Props) => {
  const {
    auth0: {
      session: { accessToken },
    },
    me: { user },
  } = props;

  const [adminOpen, setAdminOpen] = useState(false);

  const isSuperUser = user?.superUser;
  const isAdminUser = isAdmin(user);

  return (
    <aside className="bg-gray-900 text-white w-96 h-screen flex flex-col justify-between">
      <div>
        {/* Logo */}
        <div className="flex items-center gap-3 mb-6 bg-blue-400 p-4">
          <img src={Logo} alt="logo" className="w-20 h-20" />
          <span className="text-2xl font-semibold">Connect Dashboard</span>
        </div>

        <div className="p-4">
          {!!accessToken && (
            <Box className="mb-4">
              <TenantPicker />
            </Box>
          )}

          {user && (
            <List
              component="nav"
              disablePadding
              sx={{
                "& .MuiListItemText-primary": { fontSize: "1.35rem" },
                "& .MuiListItemIcon-root": { minWidth: "40px" },
                "& .MuiSvgIcon-root": { fontSize: "2rem" },
              }}
            >
              {/* Home */}
              <ListItemButton component={Link} to="/">
                <ListItemIcon className="text-white">
                  <HomeIcon
                    style={{
                      color: "#fff",
                    }}
                  />
                </ListItemIcon>
                <ListItemText primary="Home" />
              </ListItemButton>

              {/* Admin */}
              {isAdminUser && (
                <>
                  <ListItemButton onClick={() => setAdminOpen(!adminOpen)}>
                    <ListItemIcon className="text-white">
                      <AdminIcon
                        style={{
                          color: "#fff",
                        }}
                      />
                    </ListItemIcon>
                    <ListItemText primary="Admin" />
                    {adminOpen ? <ExpandLess /> : <ExpandMore />}
                  </ListItemButton>

                  <Collapse in={adminOpen} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                      {isSuperUser && (
                        <ListItemButton
                          component={Link}
                          to="/admin/tenants"
                          sx={{ pl: 4 }}
                        >
                          <ListItemIcon className="text-white">
                            <SettingsIcon
                              style={{
                                color: "#fff",
                              }}
                            />
                          </ListItemIcon>
                          <ListItemText primary="Tenants" />
                        </ListItemButton>
                      )}
                      <ListItemButton
                        component={Link}
                        to="/admin/accounts"
                        sx={{ pl: 4 }}
                      >
                        <ListItemIcon className="text-white">
                          <SettingsIcon
                            style={{
                              color: "#fff",
                            }}
                          />
                        </ListItemIcon>
                        <ListItemText primary="Accounts" />
                      </ListItemButton>
                      <ListItemButton
                        component={Link}
                        to="/admin/clientUsers"
                        sx={{ pl: 4 }}
                      >
                        <ListItemIcon className="text-white">
                          <GroupIcon
                            style={{
                              color: "#fff",
                            }}
                          />
                        </ListItemIcon>
                        <ListItemText primary="Users" />
                      </ListItemButton>
                      <ListItemButton
                        component={Link}
                        to="/admin/siteUsers"
                        sx={{ pl: 4 }}
                      >
                        <ListItemIcon className="text-white">
                          <GroupIcon
                            style={{
                              color: "#fff",
                            }}
                          />
                        </ListItemIcon>
                        <ListItemText primary="Site Users" />
                      </ListItemButton>
                      <ListItemButton
                        component={Link}
                        to="/admin/sharedCredentials"
                        sx={{ pl: 4 }}
                      >
                        <ListItemIcon className="text-white">
                          <SettingsIcon
                            style={{
                              color: "#fff",
                            }}
                          />
                        </ListItemIcon>
                        <ListItemText primary="Shared Credentials" />
                      </ListItemButton>
                      <ListItemButton
                        component={Link}
                        to="/allInvites"
                        sx={{ pl: 4 }}
                      >
                        <ListItemIcon className="text-white">
                          <LinkIcon
                            style={{
                              color: "#fff",
                            }}
                          />
                        </ListItemIcon>
                        <ListItemText primary="Invites" />
                      </ListItemButton>
                      <ListItemButton
                        component={Link}
                        to="/openDoor"
                        sx={{ pl: 4 }}
                      >
                        <ListItemIcon className="text-white">
                          <LockIcon
                            style={{
                              color: "#fff",
                            }}
                          />
                        </ListItemIcon>
                        <ListItemText primary="Open Door" />
                      </ListItemButton>
                    </List>
                  </Collapse>

                  <Divider className="my-2 border-gray-700" />

                  {/* Other Admin Links */}
                  <ListItemButton component={Link} to="/admin/sites">
                    <ListItemIcon className="text-white">
                      <SettingsIcon
                        style={{
                          color: "#fff",
                        }}
                      />
                    </ListItemIcon>
                    <ListItemText primary="Sites" />
                  </ListItemButton>
                  <ListItemButton
                    component={Link}
                    to="/admin/accessControlGroups"
                  >
                    <ListItemIcon className="text-white">
                      <SettingsIcon
                        style={{
                          color: "#fff",
                        }}
                      />
                    </ListItemIcon>
                    <ListItemText primary="Access Control Groups" />
                  </ListItemButton>
                  <ListItemButton component={Link} to="/admin/locks">
                    <ListItemIcon className="text-white">
                      <LockIcon
                        style={{
                          color: "#fff",
                        }}
                      />
                    </ListItemIcon>
                    <ListItemText primary="Locks" />
                  </ListItemButton>
                  <ListItemButton component={Link} to="/keyRequests">
                    <ListItemIcon className="text-white">
                      <ListIcon
                        style={{
                          color: "#fff",
                        }}
                      />
                    </ListItemIcon>
                    <ListItemText primary="Access Log" />
                  </ListItemButton>

                  {isSuperUser && (
                    <ListItemButton component={Link} to="/admin/guests">
                      <ListItemIcon className="text-white">
                        <PersonIcon
                          style={{
                            color: "#fff",
                          }}
                        />
                      </ListItemIcon>
                      <ListItemText primary="Guests" />
                    </ListItemButton>
                  )}

                  <ListItemButton component={Link} to="/admin/locks2">
                    <ListItemIcon className="text-white">
                      <LockOpenIcon
                        style={{
                          color: "#fff",
                        }}
                      />
                    </ListItemIcon>
                    <ListItemText primary="Locks V2" />
                  </ListItemButton>
                </>
              )}
            </List>
          )}
        </div>

        {!!accessToken && (
          <Box className="mt-4">
            <LoginForm />
          </Box>
        )}
      </div>
    </aside>
  );
};

export const NavMenu = connect<
  { auth0: Auth0State.Auth0State; me: MeState },
  {},
  OwnProps,
  ApplicationState
>((state) => ({
  auth0: state.auth0,
  me: state.me,
}))(NavMenuComponent);
